import * as React from "react";
import { EmailChipInput, type EmailChipInvalidReason } from "@aisquare/ui";
import { Section, VariantBlock } from "../lib/Section";

export function EmailChipInputDemo() {
  // The control is controlled, so every block that adds / removes chips owns its
  // own list. Seed two so the chip + remove-button markup is visible at rest.
  const [chips, setChips] = React.useState<string[]>([
    "ada@aisquare.studio",
    "grace@aisquare.studio",
  ]);

  // The interactive block surfaces the headless rejection seam: each invalid /
  // duplicate / over-cap entry both shakes the field (built in) and reports the
  // reason here, which a real consumer would route to a toast.
  const [invites, setInvites] = React.useState<string[]>([]);
  const [lastReject, setLastReject] = React.useState<{
    reason: EmailChipInvalidReason;
    value: string;
  } | null>(null);

  return (
    <Section
      id="email-chip-input"
      title="EmailChipInput"
      description="Validated, bulk-paste email entry that renders each address as a removable chip. A thin composer over TagInput (which owns the keyboard model, focus-within ring, and listbox ARIA) that adds a per-address validation gate, comma/newline/space paste-splitting with dedup, and a headless onInvalid callback in place of the donor screens' direct toast."
    >
      <VariantBlock label="Default" caption="empty resting state with the paste hint">
        <div className="w-[420px] max-w-full">
          <EmailChipInput label="Invite teammates" value={[]} onChange={() => {}} />
        </div>
      </VariantBlock>

      <VariantBlock label="Editable chips" caption="type + Enter to add, click x or Backspace to remove">
        <div className="w-[420px] max-w-full">
          <EmailChipInput label="Recipients" value={chips} onChange={setChips} />
        </div>
      </VariantBlock>

      <VariantBlock
        label="Validation + bulk paste"
        caption='paste "a@x.com, b@y.com" to split; invalid / duplicate / over-cap entries are rejected (max 5)'
      >
        <div className="flex w-[420px] max-w-full flex-col gap-2">
          <EmailChipInput
            label="Invite teammates"
            value={invites}
            onChange={setInvites}
            max={5}
            showInlineError
            onInvalid={(reason, value) => setLastReject({ reason, value })}
          />
          <p className="text-xs text-muted-foreground">
            {lastReject
              ? `Rejected (${lastReject.reason}): ${lastReject.value}`
              : "onInvalid reports here — a real consumer would toast it."}
          </p>
        </div>
      </VariantBlock>

      <VariantBlock label="States" caption="small size, hint hidden, and disabled">
        <div className="flex w-[420px] max-w-full flex-col gap-4">
          <EmailChipInput
            label="Small"
            size="sm"
            value={["ada@aisquare.studio"]}
            onChange={() => {}}
          />
          <EmailChipInput
            label="No hint"
            hint={null}
            value={[]}
            onChange={() => {}}
          />
          <EmailChipInput
            label="Disabled"
            disabled
            value={["ada@aisquare.studio"]}
            onChange={() => {}}
          />
        </div>
      </VariantBlock>
    </Section>
  );
}
